import React, { FC, useEffect, useState } from "react";
import { Button, Drawer } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import { projectsApis } from "../../../../api/projects";
import FormSelect from "../../../form-components/SelectComponent";
import FormMultiSelect from "../../../form-components/MultiSelectComponent";
import { getDirtyFieldsOnly } from "../../../../utils/use-form/GetDirtyFieldsOnly";

interface ProjectFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  // Tab is authoritative for status; the drawer's status field shows only on "All".
  statusTab: string;
  onApply: (data: any, count: number) => void;
  onReset: () => void;
}

const ProjectFilterDrawer: FC<ProjectFilterDrawerProps> = ({
  open,
  onClose,
  statusTab,
  onApply,
  onReset,
}) => {
  const methods = useForm({ mode: "onChange" });
  const {
    formState: { dirtyFields },
  } = methods;

  const [tagFiltersList, setTagFiltersList] = useState([]);
  const [ownerFiltersList, setOwnersFiltersList] = useState([]);
  const [groupFiltersList, setGroupsFiltersList] = useState([]);
  const [statusFilterData, setStatusFilterData] = useState([]);

  const optionsStatus = statusTab === "All" ? "Active" : statusTab;

  const loadOptions = async () => {
    try {
      const [tags, owners, groups, statuses] = await Promise.all([
        projectsApis.getTagsFilterData(optionsStatus),
        projectsApis.getOwnersFilterData(optionsStatus),
        projectsApis.getGroupsFilterData(optionsStatus),
        projectsApis.fetchStatusFiltersByModule("Activity"),
      ]);
      if (tags?.status) {
        setTagFiltersList(
          tags.data.map((item: any) => ({
            label: item.tagName,
            value: item.tagId,
          }))
        );
      }
      if (owners?.status) {
        setOwnersFiltersList(
          owners.data.map((item: any) => ({
            label: item.userName,
            value: item.userId,
          }))
        );
      }
      if (groups?.status) {
        setGroupsFiltersList(
          groups.data.map((item: any) => ({
            label: item.projectGroupName,
            value: item.projectGroupId,
          }))
        );
      }
      if (statuses?.status) {
        setStatusFilterData(
          statuses.data.map((item: any) => ({ label: item, value: item }))
        );
      }
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  useEffect(() => {
    if (open) loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleReset = () => {
    methods.reset();
    onReset();
  };

  return (
    <Drawer
      maskClosable={false}
      title={
        <div className="d-flex justify-content-between align-items-center">
          <strong>Filters</strong>
          <Button type="link" size="small" onClick={handleReset}>
            Reset
          </Button>
        </div>
      }
      placement="right"
      width={450}
      onClose={onClose}
      open={open}
      styles={{ body: { paddingBottom: 80 } }}
    >
      <div className="d-flex align-items-center flex-wrap w-100">
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit((data) => {
              const dirtyData = getDirtyFieldsOnly(data, dirtyFields);
              const count =
                (dirtyData !== undefined && Object.keys(dirtyData).length) || 0;
              onApply(data, count);
            })}
            className="w-100"
          >
            <div key={"filters"} className="mb-2 mt-0">
              <FormMultiSelect
                name={`group`}
                label={"Group"}
                placeholder={"By Group"}
                options={groupFiltersList}
              />
            </div>
            <div key={"filters2"} className="mb-2 mt-0">
              <FormMultiSelect
                name={`owner`}
                label={"Owner"}
                placeholder={"By Owner"}
                options={ownerFiltersList}
              />
            </div>
            <div key={"filters3"} className="mt-0 mb-2">
              <FormSelect
                name={`completion`}
                label={"Completion %"}
                placeholder={"By Completion %"}
                options={Array.from({ length: 20 }, (_, i) => {
                  const start = i * 5 + 1;
                  const end = (i + 1) * 5;
                  return {
                    value: `${start}-${end}`,
                    label: `${start}–${end}%`,
                  };
                })}
              />
            </div>
            <div key={"filters4"} className="mt-0 mb-2">
              <FormMultiSelect
                name={`tag`}
                label={"Tag"}
                placeholder={"By Tag"}
                options={tagFiltersList}
              />
            </div>

            {statusTab === "All" && (
              <div key={"filters6"} className="mt-0 mb-2">
                <FormMultiSelect
                  name={`status`}
                  label={"Status"}
                  placeholder={"By Status"}
                  options={statusFilterData}
                />
              </div>
            )}

            <div className="actions clearfix mt-3">
              <ul className="d-flex gap-2 list-unstyled">
                <li>
                  <Button type="primary" htmlType="submit">
                    Apply Filters
                  </Button>
                </li>
                <li>
                  <Button onClick={handleReset}>Cancel</Button>
                </li>
              </ul>
            </div>
          </form>
        </FormProvider>
      </div>
    </Drawer>
  );
};

export default ProjectFilterDrawer;
